import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import { usePanel } from "hooks/usePanel";
import { GenericLoading } from "components/ui/Loader";
import { FileTypeEnum } from "api/actor";
import { FormikDropdown } from "components/forms/formik/FormikDropdown";
import { fileTypeLabels } from "features/document-view/DocumentsView";
import { DropdownItem } from "components/forms/Dropdown";
import { SupportTypeEnum } from "api/documents";
import { documentsAPI } from "services/swagger-api/documentsAPI";
import * as Yup from 'yup';
import { FormikFileUpload } from "components/forms/formik/FormikFileUpload";
import { ValidationTracker } from "components/domain/ValidationTracker/ValidationTracker";

enum ProductDocumentFormFieldNames {
  FileType = 'fileType',
  SupportType = 'supportType',
  File = 'file',
}

const productDocumentValidationSchema = Yup.object().shape({
  [ProductDocumentFormFieldNames.FileType]: Yup.number()
    .oneOf(
      [
        FileTypeEnum.FicheProduit,
        FileTypeEnum.Ipid,
        FileTypeEnum.Notice,
        FileTypeEnum.ConditionsSpeciales,
      ],
      'Le type de fichier sélectionné est invalide'
    )
    .required('Le type de fichier est requis'),

  [ProductDocumentFormFieldNames.SupportType]: Yup.number()
    .oneOf(
      [
        SupportTypeEnum.All,
        SupportTypeEnum.Individual,
        SupportTypeEnum.Group,
      ],
      'Le type de support sélectionné est invalide'
    )
    .required('Le type de support est requis'),

  [ProductDocumentFormFieldNames.File]: Yup.mixed()
    .required('Le fichier est requis')
    .test(
      'fileType',
      'Le format de fichier est invalide',
      (value) => {
        if (!value) return false;
        const file = value as File;
        const acceptedFormats = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        return acceptedFormats.includes(file.type);
      }
    )
    .test(
      'fileSize',
      'Le fichier est trop volumineux (max 10MB)',
      (value) => {
        if (!value) return false;
        const file = value as File;
        return file.size <= 10 * 1024 * 1024; // 10MB
      }
    ),
});

interface ProductDocumentFormValues {
  [ProductDocumentFormFieldNames.FileType]: FileTypeEnum
  [ProductDocumentFormFieldNames.SupportType]: SupportTypeEnum
  [ProductDocumentFormFieldNames.File]?: File
}


export const ProductDocumentForm: React.FC<{ productId: string }> = ({ productId }) => {
  const formikRef = useRef<FormikProps<ProductDocumentFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setPanelActions } = usePanel();


  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: "COMMON.CANCEL",
        submitLabel: "COMMON.VALIDATE",
        submitDisabled: !isFormValid,
        onSubmit: () => {
          formikRef.current?.submitForm();
        },
      });
    }
  }, [formikRef, setPanelActions, isFormValid]);

  const productDocumentReferential: DropdownItem<number>[] = useMemo(() => {
    return [
      {
        key: FileTypeEnum.FicheProduit,
        label: fileTypeLabels[FileTypeEnum.FicheProduit]
      },
      {
        key: FileTypeEnum.Ipid,
        label: fileTypeLabels[FileTypeEnum.Ipid]
      },
      {
        key: FileTypeEnum.Notice,
        label: fileTypeLabels[FileTypeEnum.Notice]
      },
      {
        key: FileTypeEnum.ConditionsSpeciales,
        label: fileTypeLabels[FileTypeEnum.ConditionsSpeciales]
      },
    ]
  }, [])

  const supportTypeReferential: DropdownItem<number>[] = useMemo(() => {
    return [
      {
        key: SupportTypeEnum.All,
        label: "Les deux"
      },
      {
        key: SupportTypeEnum.Group,
        label: "Groupe"
      },
      {
        key: SupportTypeEnum.Individual,
        label: 'Individuel'
      },
    ]
  }, [])


  const handleSubmit = useCallback(
    async (values: ProductDocumentFormValues) => {
      if (!values.file) {
        return
      }
      setIsLoading(true);
      try {
        await documentsAPI.createProductDocument({
          productId,
          supportType: values.supportType,
          fileType: values.fileType,
          file: values.file
        })
      } catch (error) {
        console.error('Error on create product document broker:', error);
      } finally {
        setIsLoading(false);
      }
    }, [productId]);

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}
      <Formik
      enableReinitialize
        initialValues={{
          [ProductDocumentFormFieldNames.FileType]: FileTypeEnum.FicheProduit,
          [ProductDocumentFormFieldNames.SupportType]: SupportTypeEnum.Individual,
        }}
        validationSchema={productDocumentValidationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {
          ({ isValid, errors }) => (
            <>
            { console.log(errors)}
              <ValidationTracker isValid={isValid} onValidationChange={(isValid: boolean) => setIsFormValid(isValid)} />
              <div className="grid grid-cols-2 gap-4">
                <FormikDropdown
                  name={ProductDocumentFormFieldNames.FileType}
                  items={productDocumentReferential}
                />
                <FormikDropdown
                  name={ProductDocumentFormFieldNames.SupportType}
                  items={supportTypeReferential}
                />
                <div className="col-span-2">
                  <FormikFileUpload
                    name={ProductDocumentFormFieldNames.File}
                    accept=".pdf"
                    placeholder="Sélectionner un fichier"
                  />
                </div>
              </div>
            </>
          )
        }
      </Formik>
    </div>
  );
};
